/**
 * Voice Selector Component
 *
 * A searchable dropdown with audio preview for each voice.
 * Inspired by ElevenLabs Voice Picker pattern.
 */

import * as React from 'react';
import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronDown, Play, Square, Search, Volume2, Loader2 } from 'lucide-react';
import { type TTSVoice } from '@/services/audioService';
import { voiceLine } from '@/hooks/useVoiceCatalog';

interface VoiceSelectorProps {
  voices: TTSVoice[];
  selectedVoice: string;
  onVoiceChange: (voiceId: string) => void;
  disabled?: boolean;
  compact?: boolean;
  className?: string;
}

export const VoiceSelector: React.FC<VoiceSelectorProps> = ({
  voices,
  selectedVoice,
  onVoiceChange,
  disabled = false,
  compact = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const [loadingVoice, setLoadingVoice] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Filter voices by search
  const filteredVoices = voices.filter((voice) => {
    const searchLower = search.toLowerCase();
    return [voice.name, voiceLine(voice), voice.persona].some((text) => text.toLowerCase().includes(searchLower));
  });

  // Get selected voice object
  const selectedVoiceObj = voices.find((v) => v.id === selectedVoice);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Stop audio when dropdown closes
  useEffect(() => {
    if (!isOpen && audioRef.current) {
      audioRef.current.pause();
      setPlayingVoice(null);
    }
  }, [isOpen]);

  const handlePlayPreview = async (e: React.MouseEvent, voice: TTSVoice) => {
    e.stopPropagation();
    e.preventDefault();

    // If already playing this voice, stop it
    if (playingVoice === voice.id) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingVoice(null);
      return;
    }

    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }

    setLoadingVoice(voice.id);
    setPlayingVoice(null);

    try {
      // Use the sampleUrl from Google Cloud directly
      const audio = new Audio(voice.sampleUrl);
      audioRef.current = audio;

      audio.addEventListener('canplaythrough', () => {
        setLoadingVoice(null);
        setPlayingVoice(voice.id);
        audio.play().catch(console.error);
      });

      audio.addEventListener('ended', () => {
        setPlayingVoice(null);
      });

      audio.addEventListener('error', () => {
        console.warn(`Voice sample not available for ${voice.id}`);
        setLoadingVoice(null);
        setPlayingVoice(null);
      });

      audio.load();
    } catch (err) {
      console.error('Failed to load voice preview:', err);
      setLoadingVoice(null);
    }
  };

  const handleSelectVoice = (voiceId: string) => {
    onVoiceChange(voiceId);
    setIsOpen(false);
    setSearch('');
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={isOpen}
          disabled={disabled}
          className={cn(
            'justify-between font-normal',
            compact ? 'h-6 text-[10px] px-2' : 'h-8 text-xs',
            className
          )}
        >
          <div className="flex items-center gap-1.5 min-w-0">
            <Volume2 className={cn('text-muted-foreground flex-shrink-0', compact ? 'h-3 w-3' : 'h-3.5 w-3.5')} />
            <span className="truncate">
              {selectedVoiceObj?.name || 'Select voice'}
            </span>
          </div>
          <ChevronDown className={cn('shrink-0 opacity-50', compact ? 'ml-1 h-3 w-3' : 'ml-2 h-3.5 w-3.5')} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[280px] p-0" align="start">
        {/* Search Input */}
        <div className="flex items-center border-b px-3 py-2">
          <Search className="h-4 w-4 text-muted-foreground mr-2" />
          <Input
            placeholder="Search voices..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 border-0 p-0 focus-visible:ring-0 text-sm"
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>

        {/* Voice List */}
        <ScrollArea className="h-[240px]">
          <div className="p-1">
            {filteredVoices.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No voices found
              </div>
            ) : (
              filteredVoices.map((voice) => {
                const isSelected = voice.id === selectedVoice;
                const isPlaying = playingVoice === voice.id;
                const isLoading = loadingVoice === voice.id;

                return (
                  <div
                    key={voice.id}
                    className={cn(
                      'flex items-center gap-2 px-2 py-2 rounded-md cursor-pointer transition-colors',
                      isSelected
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-muted'
                    )}
                    onClick={() => handleSelectVoice(voice.id)}
                  >
                    {/* Play Button */}
                    <button
                      onClick={(e) => handlePlayPreview(e, voice)}
                      className={cn(
                        'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors',
                        isPlaying
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted hover:bg-muted-foreground/20'
                      )}
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : isPlaying ? (
                        <Square className="h-3 w-3 fill-current" />
                      ) : (
                        <Play className="h-3 w-3 fill-current ml-0.5" />
                      )}
                    </button>

                    {/* Voice Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          'text-sm font-medium truncate',
                          isSelected && 'text-primary'
                        )}>
                          {voice.name}
                        </span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                          {voice.gender === 'female' ? '♀' : '♂'}
                        </span>
                        {isSelected && (
                          <span className="text-xs text-primary">Selected</span>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground truncate block">
                        {voiceLine(voice)}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default VoiceSelector;
